import { getCookie } from '@rover/rsdk/src/modules/Network/cookies';
import { getItem, setItem } from '@rover/utilities/sessionStorage';

import {
  META_DISCOUNT_CLAIM_PARAM,
  META_DISCOUNT_CLAIM_VALUE,
  META_DISCOUNT_META_UTM_CAMPAIGN,
  META_DISCOUNT_META_UTM_SOURCES,
  META_DISCOUNT_SHEET_DISMISSED_KEY,
  META_DISCOUNT_UTM_CAMPAIGN_PARAM,
  META_DISCOUNT_UTM_SOURCE_PARAM,
} from './constants';

/**
 * The smoke test's identity key: the anonymous `rover_cid` common identifier.
 * Emitted as `session_id` on analytics events and joined to `person_id`
 * server-side. Returns undefined on the server (no cookie access).
 */
export const getSessionId = (): string | undefined => getCookie('rover_cid') ?? undefined;

/**
 * Whether the page URL marks this visitor as traffic from the Meta discount
 * campaign. The geo landing page is the page the Meta ad links to, so the UTM is
 * in the query string. We require BOTH a Meta `utm_source` and the specific
 * `utm_campaign` so the offer is scoped to the campaign — not all facebook/
 * instagram traffic. Statsig is a plain holdback split and carries no UTM, so the
 * Meta gate lives here (and, re-derived from the session, in the backend signup
 * receiver, which gates on the same source + campaign).
 */
export const isFromMeta = (search: string): boolean => {
  const params = new URLSearchParams(search);
  const source = params.get(META_DISCOUNT_UTM_SOURCE_PARAM)?.toLowerCase();
  const campaign = params.get(META_DISCOUNT_UTM_CAMPAIGN_PARAM)?.toLowerCase();
  return (
    !!source &&
    META_DISCOUNT_META_UTM_SOURCES.includes(source) &&
    campaign === META_DISCOUNT_META_UTM_CAMPAIGN
  );
};

/** Whether the visitor dismissed the bottom sheet earlier in this browser session. */
export const hasDismissedBottomSheet = (): boolean =>
  getItem(META_DISCOUNT_SHEET_DISMISSED_KEY) === 'true';

export const markBottomSheetDismissed = (): void =>
  setItem(META_DISCOUNT_SHEET_DISMISSED_KEY, 'true');

/** True when the current URL carries the post-signup claim marker. */
export const hasClaimMarker = (search: string): boolean =>
  new URLSearchParams(search).get(META_DISCOUNT_CLAIM_PARAM) === META_DISCOUNT_CLAIM_VALUE;

/**
 * Build the sign-up entry URL, returning the visitor to the current page with
 * the claim marker so we can show the confirmation banner once they come back.
 *
 * The geo landing page is frontend/Cloudflare-served, so Django's
 * `ReferralTrackingMiddleware` never sees the Meta UTM and the visitor's session
 * has no `utm_attributes`. `/account/continue/` IS a Django request, so we also
 * lift `utm_source`/`utm_campaign` to the top level of that URL: the middleware
 * captures them into the (fresh) session on the GET, and the post_signup receiver
 * + `Attribution` then read them from the session. Without this the signup
 * receiver sees an empty `utm_source` and the discount is never granted. The UTM
 * stays inside `next` too so the return trip still passes the `isFromMeta` gate.
 */
export const buildSignupUrl = (pathname: string, search: string): string => {
  const original = new URLSearchParams(search);
  const returnParams = new URLSearchParams(search);
  returnParams.set(META_DISCOUNT_CLAIM_PARAM, META_DISCOUNT_CLAIM_VALUE);
  const next = `${pathname}?${returnParams.toString()}`;
  const params = new URLSearchParams({ action: 'sign_up', next });
  [META_DISCOUNT_UTM_SOURCE_PARAM, META_DISCOUNT_UTM_CAMPAIGN_PARAM].forEach((key) => {
    const value = original.get(key);
    if (value) params.set(key, value);
  });
  return `/account/continue/?${params.toString()}`;
};
